import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useEmploymentContractsSave } from "@/queries/employment-contracts.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmploymentContract } from "@/types/employment-contracts.types.ts";
import { Button, Input, useModals, Modal, ModalProps, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface EmploymentContractEditModelProps extends ModalProps {
  employmentContract: ApiEmploymentContract | undefined;
}

export function EmploymentContractEditModal({
  employmentContract,
  ...props
}: EmploymentContractEditModelProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();

  const EmploymentContractSchemaObject = z.object({
    title: z.string().min(1, t("errors:field_required")),
  });
  type EmploymentContractSchema = z.infer<typeof EmploymentContractSchemaObject>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EmploymentContractSchema>({
    mode: "onSubmit",
    resolver: zodResolver(EmploymentContractSchemaObject),
    values: {
      title: employmentContract?.title ?? "",
    },
    defaultValues: {
      title: "",
    },
  });

  const employmentContractsSave = useEmploymentContractsSave(control);

  const onSubmit = handleSubmit(async (schema) => {
    try {
      const data = await employmentContractsSave.mutateAsync({
        id: employmentContract?.id,
        payload: { title: schema.title },
      });

      if (employmentContract?.id) {
        analyticsService.trackEvent(analyticEvents.employmentContracts.edited, {
          [analyticProperties.id]: employmentContract.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.employmentContracts.created, {
          [analyticProperties.id]: data.id,
        });
      }

      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("core:employment_contract")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={employmentContractsSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
